import { useEffect } from "react";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, setSEO } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Marquee";
import { CORE_VALUES, TIMELINE } from "@/lib/data";

const Story = () => (
    <section className="py-28 md:py-36" data-testid="about-story">
        <div className="sf-container grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-7">
                <SectionHeading overline="Our Story" title="Engineering Excellence Since 2007" />
                <Reveal delay={0.15}>
                    <div className="mt-10 space-y-6 text-sf-silver text-base leading-relaxed max-w-2xl">
                        <p>
                            Super Finishers was founded in 2007 by Mr. Rajeev Gupta with the objective of serving the industry
                            through the manufacturing of high-quality precision tubular components.
                        </p>
                        <p>
                            After successful growth in precision tubular components for the shock absorber industry, the company
                            expanded in 2013 with the establishment of its Cycle Parts Division.
                        </p>
                        <p className="text-white/80">
                            Today, our focus remains unchanged: innovation, engineering, precision manufacturing, consistent
                            quality and customer satisfaction — supported by sophisticated machines, high-quality raw materials
                            and dedicated teams in R&D, quality management, business development and production.
                        </p>
                    </div>
                </Reveal>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
                <Reveal delay={0.25}>
                    <div className="border border-white/10 p-10 bg-sf-surface" data-testid="about-motto-card">
                        <p className="sf-overline">Our Motto</p>
                        <p className="font-display font-extrabold text-3xl md:text-4xl text-white leading-tight mt-6">
                            &ldquo;SUCCESS THROUGH <span className="text-sf-green">QUALITY</span>&rdquo;
                        </p>
                        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mt-8">ISO 9001:2015 Certified</p>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

const Manifesto = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="about-values">
        <div className="sf-container">
            <SectionHeading overline="Core Values" title="What We Stand For" />
            <div className="mt-16 border-t border-white/10">
                {CORE_VALUES.map((v, i) => (
                    <Reveal key={v.num} delay={i * 0.06}>
                        <div className="group grid grid-cols-12 gap-4 items-baseline py-10 border-b border-white/10 px-2 md:px-6 hover:bg-sf-surface transition-colors duration-500" data-testid={`value-${v.num}`}>
                            <span className="col-span-2 md:col-span-1 font-mono text-sf-green text-sm">{v.num}</span>
                            <h3 className="col-span-10 md:col-span-5 font-display font-bold text-2xl md:text-3xl text-white group-hover:text-sf-green transition-colors duration-500">
                                {v.title}
                            </h3>
                            <p className="col-span-10 col-start-3 md:col-span-5 md:col-start-8 text-sf-silver text-sm leading-relaxed">{v.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const FounderSection = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="about-founder">
        <div className="sf-container grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
                <Reveal>
                    <div className="sf-image-frame max-w-md" data-testid="about-founder-image">
                        <img src="/founder.webp" alt="Mr. Rajeev Gupta — Founder and Chairman" className="w-full aspect-[3/4] object-cover object-top" />
                        <div className="absolute bottom-0 left-0 bg-sf-green text-sf-black font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2">
                            Est. 2007
                        </div>
                    </div>
                </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
                <SectionHeading overline="Founder & Chairman" title="Rajeev Gupta" />
                <Reveal delay={0.15}>
                    <div className="mt-8 space-y-6 text-sf-silver text-base leading-relaxed">
                        <p className="text-white/80">
                            Rajeev Gupta holds a Bachelor&apos;s degree in Electrical Engineering and has more than two decades of
                            experience in technical and management positions.
                        </p>
                        <p>
                            Under his professional guidance and experience, Super Finishers has developed an efficient operational
                            management system. His mission is to make Super Finishers an industry leader in providing unmatched
                            quality while integrating innovation into product development.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mt-12">
                        {[
                            { k: "20+", v: "Years of Experience" },
                            { k: "B.E.", v: "Electrical Engineering" },
                        ].map((s) => (
                            <div key={s.v} className="bg-sf-black p-8" data-testid={`founder-stat-${s.k.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                                <p className="font-display font-extrabold text-3xl text-sf-green">{s.k}</p>
                                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mt-2">{s.v}</p>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

const JourneyStrip = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="about-journey">
        <div className="sf-container">
            <SectionHeading overline="Milestones" title="The Journey So Far" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-16">
                {TIMELINE.map((t, i) => (
                    <Reveal key={t.year} delay={i * 0.12} className="bg-sf-black">
                        <div className="p-10 h-full hover:bg-sf-surface transition-colors duration-500" data-testid={`about-timeline-${t.year.toLowerCase()}`}>
                            <p className="font-display font-extrabold text-5xl text-sf-green">{t.year}</p>
                            <h3 className="font-display font-bold text-lg text-white mt-6">{t.title}</h3>
                            <p className="text-sf-silver text-sm leading-relaxed mt-3">{t.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

export default function About() {
    useEffect(() => {
        setSEO("About Us — Super Finishers", "Founded in 2007 by Rajeev Gupta, Super Finishers manufactures precision tubular components, automotive components and cycle parts. ISO 9001:2015 certified.");
    }, []);

    return (
        <>
            <PageHero
                overline="About Us"
                titleLines={["Success", "Through Quality."]}
                description="A precision manufacturing company built on innovation, engineering and an uncompromising commitment to quality — since 2007."
                image="components"
            />
            <Story />
            <EditorialMarquee />
            <Manifesto />
            <FounderSection />
            <JourneyStrip />
        </>
    );
}
